import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  forwardRef,
  ChangeDetectionStrategy,
  AfterViewInit,
  Renderer2,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { RdsIconsModule } from '@rds/angular-components';

// A custom rich text editor component leveraging a native `contenteditable` div.
// Implements `ControlValueAccessor` for seamless integration with Angular forms (Template-driven and Reactive).

@Component({
  selector: 'app-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true,
    },
  ],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RdsIconsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RichTextEditorComponent
  implements ControlValueAccessor, AfterViewInit {
  @ViewChild('editor') editor!: ElementRef<HTMLDivElement>;

  @Input() placeholder: string = 'Enter text...';
  @Input() readonly: boolean = false;
  @Input() minHeight: number = 400;
  @Input() invalid = false;

  private undoStack: string[] = [];
  private redoStack: string[] = [];
  value: string = '';

  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  // Guards to prevent infinite loops or unwanted side-effects during DOM manipulation and async state restorations.
  private isConverting = false;
  private isRestoring = false;

  constructor(private renderer: Renderer2) {}

  ngAfterViewInit(): void {
    if (this.editor) {
      // Initialize editor with current value
      this.editor.nativeElement.innerHTML = this.value;

      // Intercept paste to force plain text insertion
      this.editor.nativeElement.addEventListener(
        'paste',
        (event: ClipboardEvent) => this.onPaste(event),
      );

      // Handle specific key interactions natively (e.g., Backspace in lists)
      this.editor.nativeElement.addEventListener(
        'keydown',
        (event: KeyboardEvent) => {
          if (event.key === 'Backspace') this.handleBackspace(event);
        },
      );

      // Listen to all manual user input to sync state and format
      this.editor.nativeElement.addEventListener('input', () => {
        this.saveHistory();
        this.normalizeLists();
        this.updateValue();
      });

      this.normalizeLists();
    }
  }

  // Prevents standard backspace behavior in empty list items to cleanly remove.
  private handleBackspace(event: KeyboardEvent) {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);

    // Get the element containing the cursor
    const startNode = range.startContainer;
    const element =
      startNode.nodeType === Node.ELEMENT_NODE
        ? (startNode as Element)
        : startNode.parentElement;
    if (!element) return;

    const li = element.closest('li');

    if (!li) return;

    // If the list item is completely empty, delete it entirely and prevent default backspace
    if (li.textContent?.trim() === '') {
      event.preventDefault();
      const parentList = li.parentElement!;
      li.remove();

      // If that was the last item, remove the entire list container
      if (parentList.children.length === 0) parentList.remove();
    }
  }

  // ---------------- ControlValueAccessor ----------------

  // Called by Angular forms to write a value to the view.
  writeValue(value: string): void {
    this.value = value || '';
    if (this.editor) {
      this.editor.nativeElement.innerHTML = this.value;
      this.normalizeLists();
    }
  }

  // Registers a callback function that is called when the control's value changes in the UI.
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // Registers a callback function that is called on initialization to update the form model.
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // Called by the forms API when the control status changes to or from 'DISABLED'.
  setDisabledState(isDisabled: boolean): void {
    this.readonly = isDisabled;
  }

  // Used to manually mark the control as touched (e.g., on blur events).
  markAsTouched(): void {
    this.onTouched();
  }

  // Handler for general input events. Guarded against triggering during DOM conversion.
  public onInput(): void {
    if (this.isConverting) return;

    this.normalizeLists();
    this.updateValue();
  }

  // ---------------- FORMATTING ----------------
  // Uses native `execCommand` for basic inline styles but uses custom DOM manipulation for complex structural changes.
  public format(command: string, value: any = null): void {
    if (!this.editor) return;

    const selection = window.getSelection();

    if (!selection || !selection.rangeCount) {
      return;
    }

    // Custom handling for Unordered Lists to prevent native nested list bugs
    if (command === 'insertUnorderedList') {
      const selection = window.getSelection();
      let node: any = selection?.anchorNode;

      while (node && node.nodeName !== 'OL') {
        node = node.parentNode;
      }

      // If already inside an OL, safely convert it to a UL
      if (node?.nodeName === 'OL') {
        this.convertOlToUl();
      } else {
        // Otherwise, allow native UL creation
        document.execCommand('insertUnorderedList', false, value);
        this.normalizeLists();
        this.updateValue();
      }
      return;
    }

    // Custom handling for Ordered Lists
    if (command === 'insertOrderedList') {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);

      let node: Node | null = range.startContainer;
      if (node.nodeType === Node.TEXT_NODE) {
        node = node.parentElement;
      }

      const list = (node as HTMLElement)?.closest?.('ol');

      // If already inside an OL, unwrap the list back into standard paragraphs/divs
      if (list) {
        this.unwrapList(list as HTMLElement);
      } else {
        document.execCommand('insertOrderedList', false);
      }

      this.normalizeLists();
      this.updateValue();
      return;
    }

    // Handle indentation natively
    if (['indent', 'outdent'].includes(command)) {
      this.handleIndentation(command);
      return;
    }

    // Standard inline formatting (bold/italic/underline)
    if (['bold', 'italic', 'underline'].includes(command)) {
      document.execCommand(command, false, value);
      this.updateValue();
      this.normalizeLists();
      return;
    }

    // Fallback for any other commands
    this.normalizeLists();
    this.updateValue();
  }

  // Transforms an Ordered List (<ol>) into an Unordered List (<ul>)
  private convertOlToUl() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    this.isConverting = true; // Lock input listeners during structural changes

    const range = selection.getRangeAt(0);
    let node: any = selection.anchorNode;

    // Traverse up to find the OL container
    while (node && node.nodeName !== 'OL') {
      node = node.parentNode;
    }

    if (!node) {
      this.isConverting = false;
      return;
    }

    const olElement = node as HTMLOListElement;
    selection.removeAllRanges();

    // Create a new UL and move all children from the OL into it
    const ulElement = document.createElement('ul');
    while (olElement.firstChild) {
      ulElement.appendChild(olElement.firstChild);
    }

    // Replace the OL with the new UL in the DOM
    olElement.parentNode?.replaceChild(ulElement, olElement);

    // Asynchronously restore the user's cursor selection inside the new UL
    requestAnimationFrame(() => {
      try {
        const sel = window.getSelection();
        if (!sel) return;

        sel.removeAllRanges();

        const newRange = document.createRange();
        newRange.selectNodeContents(ulElement);
        newRange.collapse(false); // Move cursor to the end

        sel.addRange(newRange);
      } catch {}

      setTimeout(() => {
        this.isConverting = false; // Release lock
        this.normalizeLists();
        this.updateValue();
      }, 0);
    });
  }

  // Flattens a list by converting its <li> items into standalone <div> elements, effectively removing the list formatting.
  private unwrapList(list: HTMLElement): void {
    const parent = list.parentNode;
    if (!parent) return;

    const fragment = document.createDocumentFragment();

    Array.from(list.querySelectorAll('li')).forEach((li) => {
      const div = document.createElement('div');
      div.innerHTML = li.innerHTML;
      fragment.appendChild(div);
    });

    parent.replaceChild(fragment, list);
  }

  // ---------------- STANDARD EDITING CONTROLS ----------------
  public cut(): void {
    document.execCommand('cut');
  }

  public copy(): void {
    document.execCommand('copy');
  }

  // Async programmatic paste (triggered via toolbar button) and inserts as plain text.
  public async paste(): Promise<void> {
    if (!this.editor) return;

    try {
      const text = await navigator.clipboard.readText();

      const selection = window.getSelection();
      if (!selection || !selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      range.deleteContents(); // Remove currently selected text

      const textNode = document.createTextNode(text);
      range.insertNode(textNode);

      // Move cursor right after the pasted text
      range.setStartAfter(textNode);
      range.collapse(true);

      selection.removeAllRanges();
      selection.addRange(range);

      this.updateValue();
    } catch (error) {
      console.warn('Clipboard paste failed', error);
    }
  }

  // Reverts to the previous state stored in the custom undo stack.
  public undo(): void {
    if (this.undoStack.length <= 1) return;

    const current = this.undoStack.pop();
    if (current) {
      this.redoStack.push(current);
    }

    const previous = this.undoStack[this.undoStack.length - 1];
    if (previous) {
      this.restoreHistory(previous);
    }
  }

  // Applies the next state stored in the custom redo stack.
  public redo(): void {
    const next = this.redoStack.pop();
    if (!next) return;

    this.undoStack.push(next);
    this.restoreHistory(next);
  }

  // Snapshots the current editor HTML and pushes it to the undo stack.
  // Limits history to 100 states to prevent memory bloat.
  private saveHistory(): void {
    if (this.isRestoring || !this.editor) return;

    const html = this.editor.nativeElement.innerHTML;

    // Only save if the state actually changed
    if (this.undoStack[this.undoStack.length - 1] !== html) {
      this.undoStack.push(html);

      if (this.undoStack.length > 100) {
        this.undoStack.shift(); // Remove oldest entry
      }

      // Clear redo stack on new action
      this.redoStack = [];
    }
  }

  // Restores a specific HTML string from the history stack.
  private restoreHistory(html: string): void {
    this.isRestoring = true;

    this.editor.nativeElement.innerHTML = html;
    this.normalizeLists();
    this.updateValue();

    requestAnimationFrame(() => {
      this.isRestoring = false;
    });
  }

  // ---------------- PASTE PLAIN TEXT ----------------
  // Intercepts the native Ctrl+V / Cmd+V paste event to strip formatting and plain text insertion.
  private onPaste(event: ClipboardEvent): void {
    if (!this.editor) return;

    event.preventDefault(); // Stop native HTML paste

    const text = event.clipboardData?.getData('text/plain') || '';
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    const textNode = document.createTextNode(text);
    range.insertNode(textNode);

    // Restore cursor position
    range.setStartAfter(textNode);
    range.collapse(true);

    selection.removeAllRanges();
    selection.addRange(range);

    this.updateValue();
    this.normalizeLists();
  }

  // ---------------- LIST NORMALIZATION ----------------
  private normalizeLists(): void {
    if (!this.editor) return;

    const lists = this.editor.nativeElement.querySelectorAll('ul,ol');

    lists.forEach((listEl) => {
      const list = listEl as HTMLElement;
      list.removeAttribute('style');
    });
  }

  // Syncs the native DOM HTML back to the Angular component state.
  private updateValue(): void {
    if (!this.editor) return;

    const html = this.editor.nativeElement.innerHTML;
    if (html !== this.value) {
      this.value = html;
      this.onChange(html);
    }
  }

  // Handles indent and outdent commands natively but ensures the DOM is normalized.
  private handleIndentation(command: string): void {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) {
      return;
    }

    const range = selection.getRangeAt(0);
    let node = range.startContainer;

    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentElement!;
    }

    const list = (node as HTMLElement)?.closest('ul,ol');

    if (list) {
      // Use browser native behavior inside lists
      document.execCommand(command, false);
    } else {
      // Use browser native behavior for normal text blocks
      document.execCommand(command, false);
    }

    // Wait for native command to finish rendering before cleaning up
    requestAnimationFrame(() => {
      this.normalizeLists();
      this.updateValue();
      this.saveHistory();
    });
  }
}
